import { Controller, UseFormReturn, useWatch } from 'react-hook-form';
import { useFormatter, useTranslations } from 'use-intl';
import type { SetRangeFormFields } from '../types';
import { getPolaritySign } from '../utils';
import { calculatePriceFromTick } from '@/domain/ticks/helpers';
import { WeiPerEther } from 'nftx.js';
import { formatEther } from 'viem';

type Form = UseFormReturn<SetRangeFormFields>;

export interface Props {
  poolType: 'pair' | 'eth' | 'nft';
  control: Form['control'];
  canEditRange: boolean;
  setDisabled: (v: boolean) => void;
  onMinPercentChange: (v: number) => void;
  onMaxPercentChange: (v: number) => void;
  percentages: number[];
  name: 'minPercent' | 'maxPercent';
  priceInEthKey: 'minPriceInEth' | 'maxPriceInEth';
  tick: string;
  tickKey: 'tickLower' | 'tickUpper';
  isVToken0: boolean;
}

// Based on the current tick, calculate the (rough) percentage-difference of the tickUpper/tickLower field
const calculateCurrentPercentage = (
  isVToken0: boolean,
  field: string | number,
  tick: string,
  tickKey: 'tickLower' | 'tickUpper',
  poolType: 'pair' | 'eth' | 'nft'
) => {
  const tickA = Number(field || '0');
  const tickB = Number(tick || '0');
  let priceA = calculatePriceFromTick(tickA, isVToken0);
  let priceB = calculatePriceFromTick(tickB, isVToken0);

  let exactCurrentPercentage = (priceA * WeiPerEther) / priceB;
  if (tickKey === 'tickUpper' || poolType === 'nft') {
    exactCurrentPercentage -= WeiPerEther;
  } else {
    exactCurrentPercentage = WeiPerEther - exactCurrentPercentage;
  }

  const currentPercentage =
    Math.round(
      Math.round(Number(formatEther(exactCurrentPercentage)) * 1000) / 10
    ) / 100;

  return currentPercentage;
};

// Determine whether or not a percentage is "close enough" to the current percentage to be deemed the same
const isWithinPercentageThreshold = (p: number, currentPercentage: number) => {
  const offset = Math.abs(p - currentPercentage);
  return offset < 0.1;
};

export const PricePercentage = ({
  control,
  poolType,
  canEditRange,
  setDisabled,
  onMinPercentChange,
  onMaxPercentChange,
  percentages,
  name,
  tickKey,
  tick,
  priceInEthKey,
  isVToken0,
}: Props) => {
  const { number } = useFormatter();

  const { price = '0', fullRange = false } = useWatch({
    control,
  });

  const hasPrice = !!price && !!Number(price) && !Number.isNaN(Number(price));

  if (!canEditRange) {
    return null;
  }

  return (
    <div className="grid grid-cols-4 gap-2 mt-2">
      <Controller
        name={tickKey}
        control={control}
        render={({ field }) => {
          const currentPercentage = calculateCurrentPercentage(
            isVToken0,
            field.value,
            tick,
            tickKey,
            poolType
          );

          return (
            <>
              {percentages.map((p) => (
                <button
                  type="button"
                  className={`${
                    isWithinPercentageThreshold(p, currentPercentage)
                      ? 'border-pink-500 bg-pink-500/10'
                      : 'border-gray-300 hover:bg-pink-500/5 hover:border-pink-500/50'
                  } border rounded-md p-1 transition-colors text-sm xl:text-base`}
                  key={p}
                  onClick={() => {
                    setDisabled(true);
                    if (name === 'minPercent') {
                      onMinPercentChange(p);
                    } else {
                      onMaxPercentChange(p);
                    }
                  }}
                  disabled={fullRange || !hasPrice}
                >
                  {getPolaritySign(priceInEthKey, poolType)}
                  {number(p, { style: 'percent' })}
                </button>
              ))}
              <span
                className={`${
                  percentages.some((p) =>
                    isWithinPercentageThreshold(p, currentPercentage)
                  ) === false
                    ? 'border-pink-500 bg-pink-500/10'
                    : 'border-gray-300 hover:bg-pink-500/5 hover:border-pink-500/50'
                } flex border rounded-md p-1 transition-colors items-center`}
              >
                <span className="px-1">
                  {getPolaritySign(priceInEthKey, poolType)}
                </span>
                <input
                  className="flex-1 min-w-0 w-full bg-transparent text-center placeholder:text-gray-400 outline-0 focus:outline-0 focus:ring-0 p-1 border-none"
                  placeholder="0"
                  type="number"
                  disabled={fullRange || !hasPrice}
                  onChange={(e) => {
                    setDisabled(true);
                    if (name === 'minPercent') {
                      const value = Math.min(1, Number(e.target.value) / 100);
                      onMinPercentChange(value);
                    } else {
                      const value = Number(e.currentTarget.value) / 100;
                      onMaxPercentChange(value);
                    }
                  }}
                />
                <span className="px-1">{'%'}</span>
              </span>
            </>
          );
        }}
      />
    </div>
  );
};

export default PricePercentage;
